import { computed, inject, Injectable, signal } from '@angular/core';
import { BookingService } from '../services/resources/booking.service';
import { TimestampRange } from './archive-search.types';
import { Booking } from './models';
import { buildHaystack, matchesSearchTerm, normalize } from './search-utils';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  timer,
} from 'rxjs';

type SearchableBooking = Booking & { __haystack: string };

@Injectable({ providedIn: 'root' })
export class ArchiveSearchStore {
  private readonly bookingServce = inject(BookingService);

  //Inputs and states
  readonly range = signal<TimestampRange | null>(null);
  readonly queryText = signal<string>('');

  //Ui state
  readonly isLoading = signal<boolean>(false);
  readonly errorMessage = signal<string | null>(null);

  //Cache
  readonly allInRange = signal<SearchableBooking[]>([]);

  //Visible Limit
  readonly visibleLimit = signal(100);

  //Derived List: instant filtering
  readonly filtered = computed(() => {
    const bookings = this.allInRange();
    const query = this.debouncedQuery();
    const limit = this.visibleLimit();

    if (!query.trim()) return bookings.slice(0, limit);

    const hits = bookings.filter((b) => {
      return matchesSearchTerm(b.__haystack, query, 'and');
    });

    return hits.slice(0, limit);
  });

  loadMore(): void {
    this.visibleLimit.update((n) => n + 100);
  }

  resetLimit(): void {
    this.visibleLimit.set(100);
  }

  private readonly debouncedQuery = toSignal(
    toObservable(this.queryText).pipe(
      map((v) => normalize(v)),
      debounceTime(200),
      distinctUntilChanged(),
      switchMap((q) => {
        if (!q) return of(q);
        return timer(200).pipe(map(() => q));
      }),
    ),
    { initialValue: '' },
  );

  async setRangeAndFetch(range: TimestampRange): Promise<void> {
    const prev = this.range();
    if (prev && this.sameRange(prev, range)) return;

    this.visibleLimit.set(100);
    this.range.set(range);
    this.isLoading.set(true);
    this.errorMessage.set(null);
    try {
      const items = await this.bookingServce.getSubBookingsForRangeOnce(range);
      this.allInRange.set(
        items.map((b) => ({
          ...b,
          __haystack: buildHaystack(b),
        })),
      );
    } catch (e: unknown) {
      this.allInRange.set([]);
      this.errorMessage.set(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      this.isLoading.set(false);
    }
  }

  private sameRange(a: TimestampRange, b: TimestampRange): boolean {
    return (
      a.start.seconds === b.start.seconds &&
      a.start.nanoseconds === b.start.nanoseconds &&
      a.end.seconds === b.end.seconds &&
      a.end.nanoseconds === b.end.nanoseconds
    );
  }
}
